import React from "react";
import { useTranslation } from 'react-i18next';
import { Box, Chip, ListItemButton, ListItemAvatar, Avatar, Typography } from "@mui/material";
import getImageUrl from "../../../../utils/getImageUrl";
import { TbLink, TbUsers, TbChevronRight, TbShoppingCartCheck, TbTag } from "react-icons/tb";
import createPriceString from "../../../common/common/CreatePriceStr";
import ShareEarnButton from "../../../Affiliate/ShareEarnButton";

export default function URLCard({ item, windowWidth, openBuyNowCheckout, openURLProductView, influencer, currency, current_user, mobilePreview, is_me }) {
  const { t } = useTranslation();
  const isCoachingCall = item.content_type === 'urc' || item.content_type === 'urw';

  // item.content_price > 0 means paid; item.current_tier === 5 means access granted
  const isPaid = item.content_price > 0;
  const hasAccess = item.current_tier === 5;
  const isLocked = isPaid && !hasAccess;

  const isDmUrl = item.content_type === 'dm_url' || item.delivery_type === 'url';

  const handleClick = () => {
    if (mobilePreview) return;
    // URL-delivery custom products — show sales page, or open URL if already purchased
    if (isDmUrl) {
      if (hasAccess) {
        const dmUrl = item.url || item.text;
        if (dmUrl) {
          window.open(dmUrl.startsWith('http') ? dmUrl : 'https://' + dmUrl, '_blank');
        }
      } else if (openURLProductView) {
        openURLProductView(item.id);
      }
      return;
    }
    // Regular URL/coaching call products
    if (isLocked) {
      if (!current_user) {
        if (openURLProductView) {
          openURLProductView(item.id);
        } else {
          try { window.location.assign(`/register?post_url=${influencer}&mode=buynow&id=${item.id}`); } catch (e) {}
        }
        return;
      }
      if (openURLProductView) {
        openURLProductView(item.id);
      } else if (openBuyNowCheckout) {
        openBuyNowCheckout(currency, item.thumbnail, item.name, 'url', item.id);
      }
    } else if (item.text) {
      window.open(item.text.startsWith('http') ? item.text : 'https://' + item.text, '_blank');
    }
  };

  const priceStr = isPaid ? createPriceString(item.content_price, currency) : null;
  const affiliateProductType = item.content_type === 'urw' ? 'webinar' : (isCoachingCall ? 'coaching_call' : 'url');
  const showShareButton = !is_me && !mobilePreview && item.affiliate_enabled === true && influencer;

  return (
    <ListItemButton
      sx={{
        borderRadius: "12px",
        margin: "40px auto 0px auto",
        maxWidth: "95%",
        border: '1px solid #d1d5db',
        backgroundColor: "#fff",
        alignItems: "center",
        position: "relative",
        p: 2,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        transition: "transform 200ms cubic-bezier(0.4,0,0.2,1), box-shadow 150ms cubic-bezier(0.4,0,0.2,1)",
        transform: "scale(1)",
        "&:hover": mobilePreview ? {} : {
          transform: "scale(1.01)",
          backgroundColor: "#f3f4f6",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)"
        },
      }}
      onClick={handleClick}
    >
      <ListItemAvatar sx={{ mr: 1.25, minWidth: "auto" }}>
        {item.thumbnail ? (
          <Avatar
            variant="rounded"
            sx={{ width: 52, height: 52, backgroundColor: '#eff6ff', color: '#3b82f6' }}
            src={getImageUrl(item.thumbnail)}
          />
        ) : (
          <Avatar
            variant="rounded"
            sx={{ width: 52, height: 52, backgroundColor: '#eff6ff', color: '#3b82f6' }}
          >
            {isCoachingCall ? <TbUsers size={24} /> : <TbLink size={24} />}
          </Avatar>
        )}
      </ListItemAvatar>
      <Box sx={{ flexGrow: 1, pr: windowWidth >= 600 ? 14 : 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: '#111827',
            letterSpacing: '-0.2px',
            lineHeight: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            width: '95%'
          }}
        >
          {item.name}
        </Typography>
        {item.text && !isLocked && (
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
              fontSize: '0.8rem',
              letterSpacing: '-0.2px',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {item.text?.substring(0, 30)}
            {item.text?.length > 30 ? '...' : ''}
          </Typography>
        )}
        {hasAccess && (
          <Chip
            icon={<TbShoppingCartCheck size={12} />}
            label={t('urlcard.purchased')}
            size="small"
            sx={{
              mt: 0.5,
              width: "fit-content",
              color: "#10b981",
              backgroundColor: "#d1fae5",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: "20px",
              "& .MuiChip-icon": { color: "#10b981", marginLeft: "4px" },
              "& .MuiChip-label": { paddingLeft: "6px", paddingRight: "8px" },
              border: "0.5px solid #10b981",
              boxShadow: "0 1px 3px rgba(16, 185, 129, 0.1)",
            }}
          />
        )}
        {isLocked && priceStr && (
          <Chip
            icon={<TbTag size={12} />}
            label={priceStr}
            size="small"
            sx={{
              mt: 0.5,
              width: "fit-content",
              color: "#3b82f6",
              backgroundColor: 'transparent',
              fontWeight: 600,
              fontSize: "0.7rem",
              height: "20px",
              "& .MuiChip-icon": { color: "#3b82f6", marginLeft: "4px" },
              "& .MuiChip-label": { paddingLeft: "6px", paddingRight: "8px" },
              border: "0.5px solid #3b82f6",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          />
        )}
        {showShareButton && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1 }}>
            <ShareEarnButton
              productType={affiliateProductType}
              productId={item.id}
              productName={item.name}
              influencer={influencer}
              hasAccess={hasAccess}
              buttonVariant="text"
              size="small"
            />
          </Box>
        )}
      </Box>
      <Box sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 1 }}>
        <TbChevronRight size={20} color="#999" />
      </Box>
    </ListItemButton>
  );
}
